/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { faBracketsCurly, faCaretDown, faCaretUp, faCopy, faEye, faMinus, faPencil, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useMemo, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
    Alert,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { getFilledArrayOrDefault, isFilledArray } from "../../../../utils";
import { toast } from "../../../../utils/toast";
import MetaForm from "../../../Forms/MetaForm";
import { Button } from "../../../Helper/Button";
import DebugModal from "../../../Helper/DebugModal";

import { default_display_config, DisplayFilterForm } from "./DisplayFilterForm";
import { addItemInArray, deleteItemInArray, moveItemDownInArray, moveItemUpInArray, updateItemInArray } from "./util";

type FilterableField = {
    display_filter: object;
    field: object;
    error?: boolean;
};

export type FilterableConfigListPropsType = {
    name: string;
    title: string;
    description: string | JSX.Element;
    getFieldTitle: (field: any, display_filter: any) => string | JSX.Element;
    getFieldForm: (field: any, updateFieldItemInArray: undefined | ((attr: string, value: any) => null)) => any[]; // Assuming any[] is the return type
    fields: FilterableField[] | object[];
    setArray: (arr: object[]) => null;
    defaultField: object;
    useDisplayFilter?: false | string[];
    error?: false | string;
}

export default function FilterableConfigList({
    name,
    title,
    description,
    getFieldTitle,
    getFieldForm,
    fields,
    setArray,
    error = false,
    defaultField,
    useDisplayFilter = ["fruit_types", "exclude_fruit_types", "layer_types", "manual_or_with_mini", "meta_conditions"]
}: FilterableConfigListPropsType) {
    const [display, setDisplay] = useState("preview");
    const [debugModal, setDebugModal] = useState(false);

    // * if no display filter is used, the array contains only the field in the list, else it is {{display_filter: {}, field: {}}
    // * if display_filter is used, make sure the items are in {{display_filter: {}, field: {}} format
    fields = (useDisplayFilter ? getFilledArrayOrDefault(fields, []).map((i) => (i.display_filter ? i : { display_filter: default_display_config, field: i })) : getFilledArrayOrDefault(fields, []).map((i) => ({ field: i, display_filter: false }))) as FilterableField[];

    const _setArray = (newList) => {
        if (useDisplayFilter) {
            setArray(newList);
        } else {
            setArray(newList.map((i: any) => i?.field));
        }
    };

    const swapFields = (fromIndex, toIndex) => {
        if (fromIndex === toIndex) return;

        const updatedFields = [...fields];
        const [movedItem] = updatedFields.splice(fromIndex, 1); // Remove the item from the array
        updatedFields.splice(toIndex, 0, movedItem); // Insert the item at the new position

        _setArray(updatedFields); // Update the state with the reordered array
    };

    const handleImportFromClipboard = (data) => {
        toast.success("Data imported from clipboard");
        _setArray(data);
    };

    const onAddItem = (newItem) => {
        addItemInArray(_setArray, fields, newItem);
        // this allows some time for the error and field to be rendered so that the scrollIntoView works properly
        setTimeout(() => {
            newItem = document.getElementById("fields-list")?.lastElementChild;
            newItem?.scrollIntoView({ behavior: "smooth" });
        }, 0);
    };

    return (
        <div className={`border p-3 mb-3 ${error && display === "preview" && "alert alert-danger"}`} css={css`${error && display === "preview" ? "" : "background:#fcfcfc"};`}>
            <div className="d-flex align-items-center gap-2">
                <div css={css`flex-shrink: 1;`} className="pe-5">
                    <div className="fw-bold" css={css`line-break: anywhere`}>{title} [{name}]</div>
                    <div className="text-muted">{description}</div>
                </div>

                <div className="ms-auto text-muted" css={css`flex-shrink: 0;`}>{fields && `${fields.length} items`}</div>

                <Button size="sm" onClick={() => setDisplay("edit")}>Edit</Button>
            </div>

            <DebugModal close={() => setDebugModal(false)} isOpen={debugModal} docProps={{ data: fields }} enableClipboardImport={true} onChange={handleImportFromClipboard} title="JSON Editor" />

            <Modal isOpen={display === "edit"} toggle={() => setDisplay("preview")} size="xl" className="modal-scrollable">
                <ModalHeader toggle={() => setDisplay("preview")}>
                    <div className="mb-0 fw-bold fs-3" css={css`line-break: anywhere`}>{title}</div>
                </ModalHeader>

                <ModalBody>
                    <div className="d-flex align-items-center justify-content-end flex-wrap gap-2 mb-3">
                        <Button size="sm" onClick={() => setDebugModal(true)}icon={faBracketsCurly} />
                        <Button size="sm" onClick={() => onAddItem(defaultField)}>Add Field</Button>
                    </div>

                    {error && (
                        <div className="mb-3">
                            <Alert color="danger">{error}</Alert>
                        </div>
                    )}

                    <div className="d-flex flex-column gap-3 scrollable" id="fields-list">
                        {fields.map((field, index) => (
                            <div key={index}>
                                <DraggableField index={index} swapFields={swapFields} itemType={name} >
                                    <FilterableConfigField
                                        item={field}
                                        useDisplayFilter={useDisplayFilter}
                                        title={getFieldTitle(field?.field, field?.display_filter)}
                                        form={getFieldForm(field?.field, ((attr: string, value: any) => updateItemInArray(_setArray, fields, index, attr, value, "field")))}
                                        setValue={(attr, value) => updateItemInArray(_setArray, fields, index, attr, value)}
                                        deleteItem={() => deleteItemInArray(_setArray, fields, index)}
                                        copyItem={() => onAddItem(field)}
                                        moveUp={() => {
                                            if (index === 0) return;
                                            moveItemUpInArray(_setArray, fields, index);
                                        }}
                                        moveDown={() => {
                                            if (index === fields.length - 1) return;
                                            moveItemDownInArray(_setArray, fields, index);
                                        }}

                                    />
                                </DraggableField>
                            </div>
                        ))}
                    </div>
                </ModalBody>
            </Modal>
        </div>
    );
}

// * Use name of the form input as itemType, so that you can only drag n drop within the same list
function DraggableField({ index, swapFields, children, itemType }) {
    const [, ref] = useDrop({
        accept: itemType,
        hover(item:any) {
            if (item.index !== index) {
                swapFields(item.index, index);
                item.index = index;
            }
        }
    });

    const [, drag] = useDrag({
        type: itemType,
        item: { index },
    });

    return (
        <div ref={(node) => drag(ref(node))}>
            {children}
        </div>
    );
}

function FilterableConfigField({
    item,
    form,
    title,
    setValue,
    moveUp,
    moveDown,
    deleteItem,
    copyItem,
    defaultDisplay = "preview",
    useDisplayFilter
}) {
    const [display, setDisplay] = useState(defaultDisplay);
    const [debugModal, setDebugModal] = useState(false);
    const [displayFilterModal, setDisplayFilterModal] = useState(false);
    const config = useConfig();
    const { display_filter, field } = item;

    const updateDisplayFilter = (field_name, value) => setValue("display_filter", { ...display_filter, [field_name]: value });
    const updateField = (field_name, value) => setValue("field", { ...field, [field_name]: value });
    const hasAdvancedAttributes = useMemo(() => form.filter((i) => i?.advanced).length > 0, [form]);

    const filteredForm = useMemo(() => form.filter((i) => {
        if (i?.advanced && display !== "advanced") {
            return false;
        }

        return true;
    }), [form, display]);

    const closeEditAndMoveUp = () => {
        setDisplay("preview");
        moveUp();
    };

    const closeEditAndMoveDown = () => {
        setDisplay("preview");
        moveDown();
    };

    const handleImportFromClipboard = (data) => {
        toast.success("Data imported from clipboard");
        setValue(data);
    };

    return (
        <div className={`p-3 border ${item.error || field.error ? "alert alert-danger" : "bg-white"}`}>
            <div className="d-flex flex-column flex-md-row flex-wrap gap-2">
                <div className="me-auto mb-1">{title}</div>

                <div className="d-flex justify-content-end justify-content-md-start align-items-center flex-wrap gap-1">
                    {useDisplayFilter && (
                        <Button size="sm" color="light" onClick={() => setDisplayFilterModal(true)} icon={faEye}>
                            {stringifyDisplayFilter(display_filter || {})}
                        </Button>
                    )}

                    <Button size="sm" color="light" onClick={() => setDebugModal(true)} icon={faBracketsCurly} />

                    <Button size="sm" color="light" onClick={() => setDisplay(display === "preview" ? "edit" : "preview")} icon={display === "preview" ? faPencil : faMinus} />

                    <Button size="sm" color="danger" outline onClick={deleteItem} icon={faTrash} />

                    <Button size="sm" color="light" onClick={copyItem} icon={faCopy} />

                    <Button size="sm" disabled={moveUp === false} color="light" onClick={closeEditAndMoveUp} icon={faCaretUp} />

                    <Button size="sm" disabled={moveDown === false} color="light" onClick={closeEditAndMoveDown} icon={faCaretDown} />
                </div>
            </div>

            <DebugModal
                close={() => setDebugModal(false)}
                isOpen={debugModal}
                docProps={{ data: item }}
                enableClipboardImport={true}
                onChange={handleImportFromClipboard}
                title="JSON Editor"
            />

            {useDisplayFilter && (
                <Modal isOpen={displayFilterModal} toggle={() => setDisplayFilterModal(false)} size="lg">
                    <ModalHeader>Display Filter</ModalHeader>

                    <ModalBody>
                        <p>When should <b>{title}</b> be visible?</p>

                        <DisplayFilterForm
                            object={display_filter}
                            setValue={updateDisplayFilter}
                            attributes={useDisplayFilter}
                            withHeader={false}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Button color="secondary" onClick={() => setDisplayFilterModal(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            )}

            {display !== "preview" && (
                <div>
                    <MetaForm
                        meta={filteredForm}
                        setValue={updateField}
                        object={field}
                        config={config}
                    />

                    <div className="d-flex justify-content-end">
                        {display === "advanced" && <div className="me-1 mb-1"><Button size="sm" color="light" onClick={() => setDisplay("edit")}>Collapse</Button></div>}
                        {display !== "advanced" && hasAdvancedAttributes && <div className="me-1 mb-1"><Button size="sm" color="light" onClick={() => setDisplay("advanced")}>Advanced</Button></div>}
                    </div>
                </div>
            )}
        </div>
    );
}

function stringifyDisplayFilter(filter: Record<string, any>): string {
    const parts: string[] = [];

    const customLabels: Record<string, string> = {
        fruit_types: "Fruits",
        exclude_fruit_types: "Exclude",
        layer_types: "Layers",
    };

    Object.entries(filter).forEach(([key, value]) => {
        if (isFilledArray(value)) {
            const label = customLabels[key] || key;
            parts.push(`${label}: ${value.join(", ")}`);
        }
    });

    return parts.length > 0 ? parts.join(" | ") : "Always visible";
}
